// Format date string for display in components other than validity
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useStateContext } from '../contexts/ContextProvider';
import { 
FaArrowLeft, 
FaEdit, 
FaTimes, 
FaCheck, 
FaClock,
FaRegCheckCircle,
FaEye,
FaTags,
FaCalendarAlt,FaTrashAlt,FaUndo
} from 'react-icons/fa';

const API_BASE_URL = 'https://apissc.sainikschoolcadet.com/api';

const TestView = () => {
const { testId } = useParams();
const navigate = useNavigate();
const { currentColor } = useStateContext();

// Test data state
const [test, setTest] = useState(null);
const [loading, setLoading] = useState(true);
const [error, setError] = useState(null);

// Test details editing state
const [isEditing, setIsEditing] = useState(false);
const [editedTest, setEditedTest] = useState({});

// Test type assignment state
const [testTypeLoading, setTestTypeLoading] = useState(false);
const [assignedTypes, setAssignedTypes] = useState([]);
const [isEditingTypes, setIsEditingTypes] = useState(false);
const [selectedTypes, setSelectedTypes] = useState([]);
const [typeAssignmentMessage, setTypeAssignmentMessage] = useState(null);

// Test validity state
const [testValidityLoading, setTestValidityLoading] = useState(false);
const [testValidity, setTestValidity] = useState({
  start_time: '',
  end_time: ''
});
const [isEditingValidity, setIsEditingValidity] = useState(false);
const [validityMessage, setValidityMessage] = useState(null);

// Available test types
const testTypeOptions = {
  'COURSE_TEST': 'Recorded Course Test',
  'SCHOLARSHIP_TEST': 'Scholarship Test',
  'TEST_OF_THE_DAY': 'Test of the Day',
  'WEEKLY_TEST': 'Live Classes Test',
  'TEST_SERIES': 'Test Series',
};


// Fetch test data on component mount
useEffect(() => {
  if (!testId || testId === 'undefined') {
    setError('Invalid test ID. Please go back and select a valid test.');
    setLoading(false);
    return;
  }
  
  fetchTestData();
  fetchTestTypeAssignments();
  fetchTestValidity();
}, [testId]);

// Fetch test data
const fetchTestData = async () => {
  try {
    setLoading(true);
    setError(null);
    
    // Fetch test details
    const testResponse = await axios.get(`${API_BASE_URL}/Tests/${testId}`);
    setTest(testResponse.data);
    setEditedTest(testResponse.data);
  } catch (err) {
    setError('Failed to load test data. Please try again.');
    console.error('Error fetching test data:', err);
  } finally {
    setLoading(false);
  }
};

// Fetch test type assignments
const fetchTestTypeAssignments = async () => {
  try {
    setTestTypeLoading(true);
    const response = await axios.get(`${API_BASE_URL}/test-type-assignments/${testId}`);
    // Map the array of assignment objects to just an array of types
    const types = response.data.map(assignment => assignment.type);
    setAssignedTypes(types || []);
    setSelectedTypes(types || []);
  } catch (err) {
    console.error('Error fetching test type assignments:', err);
    // Don't set error state here to avoid disrupting the main UI
  } finally {
    setTestTypeLoading(false);
  }
};

// Fetch test validity
const fetchTestValidity = async () => {
  try {
    setTestValidityLoading(true);
    const response = await axios.get(`${API_BASE_URL}/test-validity/${testId}`);
    
    if (response.data) {
      // Store the exact date strings as they come from the server
      setTestValidity({
        start_time: response.data.start_time || '',
        end_time: response.data.end_time || ''
      });
    } else {
      setTestValidity({
        start_time: '',
        end_time: ''
      });
    }
  } catch (err) {
    console.error('Error fetching test validity:', err);
    // Don't set error state here to avoid disrupting the main UI
  } finally {
    setTestValidityLoading(false);
  }
};
const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};
// Navigate back to folders
const goBack = () => {
  if (test && test.folder_id) {
    navigate(`/folders/${test.folder_id}`);
  } else {
    navigate('/folders');
  }
};
const handleSoftDelete = async () => {
  try {
    // Call API to soft delete the test
    const response = await fetch(`${API_BASE_URL}/tests/${test.test_id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ is_deleted: true, deleted_at: new Date() }),
    });

    if (response.ok) {
      // Update the state or inform the user that the test was soft deleted
      alert('Test soft-deleted successfully');
      // Optionally, you can reload the test data or redirect
      window.location.reload();
    } else {
      alert('Failed to soft delete the test');
    }
  } catch (error) {
    console.error('Error while soft deleting test:', error);
    alert('An error occurred while trying to delete the test.');
  }
};

// Calculate the time left for deletion
const timeLeftForDeletion = () => {
  const createdAt = new Date(test.created_at);
  const deleteWindow = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
  const timeLeft = deleteWindow - (new Date() - createdAt);

  if (timeLeft <= 0) {
    return "Time's up!";
  }

  const hours = Math.floor(timeLeft / (1000 * 60 * 60));
  const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
  return `${hours} hours ${minutes} minutes`;
};
const handleCancelDeletion = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/tests/cancel-deletion/${test.test_id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to cancel deletion');
    }

    const updatedTest = await response.json();
    // Update state or UI with the updated test data
    setTest(updatedTest.test); // Assuming you are using state to store test data
  } catch (error) {
    console.error('Error canceling deletion:', error);
  }
};

// Format date string for consistent display in local time with AM/PM
const formatDateTime = (dateString) => {
  if (!dateString) return 'Not set';
  
  // Create a date object from the UTC string - this will automatically convert to local time
  const date = new Date(dateString);
  
  // Format day, month, year
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  
  // Format hours and minutes with AM/PM
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  
  // Convert hours to 12-hour format
  hours = hours % 12;
  hours = hours ? hours : 12; // the hour '0' should be '12'
  const formattedHours = String(hours).padStart(2, '0');
  
  // Return in the desired format: DD/MM/YYYY, HH:MM:SS AM/PM
  return `${day}/${month}/${year}, ${formattedHours}:${minutes}:${seconds} ${ampm}`;
};

// Convert UTC ISO date string to local datetime-local input format
const formatForDateTimeInput = (dateString) => {
  if (!dateString) return '';
  
  // Create a date object from the UTC string - this will be in local time
  const date = new Date(dateString);
  
  // Format for datetime-local input (YYYY-MM-DDTHH:MM)
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

// Toggle publish status
const togglePublishStatus = async () => {
  try {
    setLoading(true);
    await axios.put(`${API_BASE_URL}/Tests/${testId}`, {
      ...test,
      publish_status: !test.publish_status,
      updated_at: new Date()
    });
    // Reload test data
    fetchTestData();
  } catch (err) {
    setError('Failed to update publish status. Please try again.');
    setLoading(false);
  }
};

// Handle input changes when editing test details
const handleInputChange = (e) => {
  const { name, value } = e.target;
  setEditedTest({
    ...editedTest,
    [name]: value
  });
};

// Save edited test details
const saveTestChanges = async () => {
  try {
    setLoading(true);
    await axios.put(`${API_BASE_URL}/Tests/${testId}`, {
      ...editedTest,
      updated_at: new Date()
    });
    setIsEditing(false);
    // Reload test data
    fetchTestData();
  } catch (err) {
    setError('Failed to update test. Please try again.');
    setLoading(false);
  }
};

// Cancel editing test details
const cancelEditing = () => {
  setEditedTest(test);
  setIsEditing(false);
};

// Navigate to view questions page
const navigateToViewQuestions = () => {
  navigate(`/questions/test/${testId}`);
};

// Handle test type checkbox change
const handleTypeCheckboxChange = (type) => {
  if (selectedTypes.includes(type)) {
    setSelectedTypes(selectedTypes.filter(t => t !== type));
  } else {
    setSelectedTypes([...selectedTypes, type]);
  }
};

// Save test type assignments
const saveTestTypeAssignments = async () => {
  try {
    setTestTypeLoading(true);
    setTypeAssignmentMessage(null);
    
    await axios.post(`${API_BASE_URL}/test-type-assignments`, {
      test_id: parseInt(testId),
      types: selectedTypes,
      date_assigned: new Date().toISOString().split('T')[0] // YYYY-MM-DD format
    });
    
    setAssignedTypes([...selectedTypes]);
    setIsEditingTypes(false);
    setTypeAssignmentMessage({
      type: 'success',
      text: 'Test types updated successfully!'
    });
    
    // Clear message after 3 seconds
    setTimeout(() => {
      setTypeAssignmentMessage(null);
    }, 3000);
    
  } catch (err) {
    console.error('Error updating test type assignments:', err);
    setTypeAssignmentMessage({
      type: 'error',
      text: 'Failed to update test types. Please try again.'
    });
  } finally {
    setTestTypeLoading(false);
  }
};

// Cancel editing test types
const cancelEditingTypes = () => {
  setSelectedTypes(assignedTypes);
  setIsEditingTypes(false);
};

// Handle validity input changes
const handleValidityChange = (e) => {
  const { name, value } = e.target;
  
  setTestValidity({
    ...testValidity,
    [name]: value
  });
};

// Save test validity
const saveTestValidity = async () => {
  try {
    setTestValidityLoading(true);
    setValidityMessage(null);
    
    // Validate that end_time > start_time if both are provided
    if (testValidity.start_time && testValidity.end_time) {
      const startDate = new Date(testValidity.start_time);
      const endDate = new Date(testValidity.end_time);
      if (endDate <= startDate) {
        setValidityMessage({
          type: 'error',
          text: 'End time must be after start time.'
        });
        setTestValidityLoading(false);
        return;
      }
    }

    // Convert local datetime inputs to UTC for server storage
    let startTime = null;
    let endTime = null;
    
    if (testValidity.start_time) {
      // Create Date object from local input (already in local time)
      const startDate = new Date(testValidity.start_time);
      // Convert to UTC ISO string for the server
      startTime = startDate.toISOString();
    }
    
    if (testValidity.end_time) {
      // Create Date object from local input (already in local time)
      const endDate = new Date(testValidity.end_time);
      // Convert to UTC ISO string for the server
      endTime = endDate.toISOString();
    }
    
    const apiRequest = {
      test_id: parseInt(testId),
      start_time: startTime,
      end_time: endTime
    };
    
    const response = await axios.post(`${API_BASE_URL}/test-validity`, apiRequest);
    
    // Update state with the response data (which will be in UTC)
    if (response.data) {
      setTestValidity({
        start_time: response.data.start_time || '',
        end_time: response.data.end_time || ''
      });
    }
    
    setIsEditingValidity(false);
    setValidityMessage({
      type: 'success',
      text: 'Test validity updated successfully!'
    });
    
    // Clear message after 3 seconds
    setTimeout(() => {
      setValidityMessage(null);
    }, 3000);
    
  } catch (err) {
    console.error('Error updating test validity:', err);
    setValidityMessage({
      type: 'error',
      text: 'Failed to update test validity. Please try again.'
    });
  } finally {
    setTestValidityLoading(false);
  }
};

// Cancel editing test validity
const cancelEditingValidity = () => {
  fetchTestValidity(); // Reset to original values
  setIsEditingValidity(false);
};

if (loading && !test) {
  return (
    <div className="p-6 flex justify-center items-center h-screen bg-gray-50">
      <div className="animate-pulse text-xl font-semibold text-gray-600">
        Loading test data...
      </div>
    </div>
  );
}

if (error && !test) {
  return (
    <div className="p-6 flex flex-col items-center justify-center h-screen bg-gray-50">
      <div className="text-xl font-bold text-red-500 mb-4">{error}</div>
      <button 
        className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center"
        onClick={goBack}
      >
        <FaArrowLeft className="mr-2" /> Back to Folders
      </button>
    </div>
  );
}

return (
  <div className="p-6 bg-gray-50 min-h-screen">
    {/* Back Navigation */}
    <div className="mb-6">
      <button 
        onClick={goBack}
        className="flex items-center text-gray-600 hover:text-gray-900 font-medium"
      >
        <FaArrowLeft className="mr-2" /> Back to Folders
      </button>
    </div>

    {/* Test Details Card */}
    <div className="bg-white rounded-lg shadow-md overflow-hidden mb-8">
      {/* Header with edit option */}
      <div className="flex justify-between items-center p-5 border-b bg-gray-50">
        <h1 className="text-2xl font-bold text-gray-800">
          {test?.name || 'Test Details'}
        </h1>
        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="inline-flex items-center px-3 py-1.5 bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100"
          >
            <FaEdit className="mr-2" /> Edit
          </button>
        )}
      </div>

      {/* Test details content */}
      <div className="p-6">
  {isEditing ? (
    <div className="space-y-5">
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
          Test Name
        </label>
        <input
          type="text"
          id="name"
          name="name"
          value={editedTest.name || ''}
          onChange={handleInputChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
      
      <div>
        <label htmlFor="time_duration" className="block text-sm font-medium text-gray-700 mb-1">
          Time Duration (minutes)
        </label>
        <input
          type="number"
          id="time_duration"
          name="time_duration"
          value={editedTest.time_duration || ''}
          onChange={handleInputChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
      
      <div className="flex space-x-3 pt-3">
        <button
          onClick={saveTestChanges}
          className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
          disabled={loading}
        >
          <FaCheck className="mr-2" /> Save Changes
        </button>
        <button
          onClick={cancelEditing}
          className="inline-flex items-center px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
        >
          <FaTimes className="mr-2" /> Cancel
        </button>
      </div>
    </div>
  ) : (
    <div>
      {/* Test metadata in a clean, organized layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <h3 className="text-sm font-medium text-gray-500">Time Duration</h3>
            <div className="mt-1 flex items-center">
              <FaClock className="text-gray-400 mr-2" />
              <span className="text-lg text-gray-900">
                {test?.time_duration ? `${test.time_duration} minutes` : 'No time limit'}
              </span>
            </div>
          </div>
          
          <div>
            <h3 className="text-sm font-medium text-gray-500">Created On</h3>
            <p className="mt-1 text-lg text-gray-900">
              {test?.created_at ? formatDate(test.created_at) : 'N/A'}
            </p>
          </div>
        </div>
        <div className="relative p-6">
  {/* Show 'Cancel Deletion' button only if the test is soft-deleted */}
  {test?.is_deleted && (
    <button
      onClick={handleCancelDeletion}
      className="absolute top-0 right-0 mt-4 mr-4 inline-flex items-center px-6 py-3 rounded-md text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 shadow-md bg-green-500"
    >
      <FaUndo className="mr-2" /> Cancel Deletion
    </button>
  )}
  {/* Show 'Delete Test' button only if the test is not soft-deleted */}
  {!test?.is_deleted && (
    <button
      onClick={handleSoftDelete}
      className="absolute top-0 right-0 mt-4 mr-4 inline-flex items-center px-6 py-3 rounded-md text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 shadow-md bg-red-500"
    >
      <FaTrashAlt className="mr-2" /> Delete Test
    </button>
  )}
</div>

      
        <div className="space-y-4">
          <div>
            <h3 className="text-sm font-medium text-gray-500">Publish Status</h3>
            <div className="mt-1 flex items-center">
              <div className={`flex items-center ${test?.publish_status ? 'text-green-600' : 'text-yellow-600'}`}>
                <FaRegCheckCircle className="mr-2" />
                <span className="text-lg">{test?.publish_status ? 'Published' : 'Draft'}</span>
              </div>
              <button
                onClick={togglePublishStatus}
                className={`ml-4 px-3 py-1 text-white rounded-md ${
                  test?.publish_status 
                    ? 'bg-yellow-500 hover:bg-yellow-600' 
                    : 'bg-green-500 hover:bg-green-600'
                }`}
                style={{ backgroundColor: test?.publish_status ? '#EAB308' : currentColor }}
              >
                {test?.publish_status ? 'Unpublish' : 'Publish'}
              </button>
            </div>
          </div>
          
          <div>
            <h3 className="text-sm font-medium text-gray-500">Last Updated</h3>
            <p className="mt-1 text-lg text-gray-900">
              {test?.updated_at ? formatDate(test.updated_at) : 'N/A'}
            </p>
          </div>
          
          {/* Time Left for Deletion */}
          {test?.is_deleted ? (
  <div className="mt-4">
    <h3 className="text-sm font-medium text-gray-500">Deletion Status</h3>
    <p className="mt-1 text-lg text-gray-900">
      Test will be permanently deleted in {test?.deletion_info} days.
    </p>
  </div>
) : null}
        </div>
        
      </div>
      
     
      {/* View Questions Button */}
      <div className="mt-8 flex justify-center">
        <button
          onClick={navigateToViewQuestions}
          className="inline-flex items-center px-6 py-3 rounded-md text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-md"
          style={{ backgroundColor: currentColor }}
        >
          <FaEye className="mr-2" /> View Test Questions
        </button>
      </div>
    </div>
  )}
</div>
</div>

    {/* Test Type Assignment Card */}
    <div className="bg-white rounded-lg shadow-md overflow-hidden mb-8">
      <div className="flex justify-between items-center p-5 border-b bg-gray-50">
        <h2 className="text-xl font-bold text-gray-800 flex items-center">
          <FaTags className="mr-2 text-gray-600" /> Test Type Assignment
        </h2>
        {!isEditingTypes && (
          <button
            onClick={() => setIsEditingTypes(true)}
            className="inline-flex items-center px-3 py-1.5 bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100"
          >
            <FaEdit className="mr-2" /> Edit Types
          </button>
        )}
      </div>

      <div className="p-6">
  {/* Message display */}
  {typeAssignmentMessage && (
    <div className={`mb-4 p-3 rounded-md ${
      typeAssignmentMessage.type === 'success' 
        ? 'bg-green-100 text-green-800 border border-green-200' 
        : 'bg-red-100 text-red-800 border border-red-200'
    }`}>
      {typeAssignmentMessage.text}
    </div>
  )}

  {testTypeLoading && !isEditingTypes ? (
    <div className="py-4 flex justify-center">
      <div className="animate-pulse text-gray-600">Loading test types...</div>
    </div>
  ) : isEditingTypes ? (
    <div>
      <div className="mb-4">
        <p className="text-sm text-gray-600 mb-3">Select the test types to assign to this test:</p>
        <div className="space-y-2">
          {Object.entries(testTypeOptions).map(([type, label]) => (
            <label key={type} className="flex items-center">
              <input
                type="checkbox"
                checked={selectedTypes.includes(type)}
                onChange={() => handleTypeCheckboxChange(type)}
                className="h-5 w-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
              />
              <span className="ml-2 text-gray-700">{label}</span>
            </label>
          ))}
        </div>
      </div>
      <div className="flex space-x-3 pt-3">
        <button
          onClick={saveTestTypeAssignments}
          className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
          disabled={testTypeLoading}
          style={{ backgroundColor: currentColor }}
        >
          <FaCheck className="mr-2" /> Save Types
        </button>
        <button
          onClick={cancelEditingTypes}
          className="inline-flex items-center px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
        >
          <FaTimes className="mr-2" /> Cancel
        </button>
      </div>
    </div>
  ) : assignedTypes.length > 0 ? (
    <div>
      <p className="text-sm text-gray-600 mb-2">This test is assigned to the following test types:</p>
      <div className="flex flex-wrap gap-2 mt-2">
        {assignedTypes.map((type) => (
          <span 
            key={type} 
            className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
            style={{ backgroundColor: `${currentColor}20`, color: currentColor }}
          >
            {testTypeOptions[type] || type.replace(/_/g, ' ')}
          </span>
        ))}
      </div>
    </div>
  ) : (
    <p className="text-gray-600">No test types are currently assigned to this test. Click "Edit Types" to assign test types.</p>
  )}
</div>

    </div>
    
    {/* Test Scheduling Guidance Card */}
    <div className="bg-white rounded-lg shadow-md overflow-hidden mb-8 border border-indigo-100">
      <div className="p-5 bg-indigo-50 border-b border-indigo-100 flex items-center justify-between">
        <h2 className="text-lg font-bold text-indigo-900 flex items-center">
          <FaCalendarAlt className="mr-2 text-indigo-600" /> Test Scheduling & Availability
        </h2>
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-800">
          Reusable Library Content
        </span>
      </div>
      <div className="p-6">
        <p className="text-gray-600 text-sm leading-relaxed">
          Tests in the Test Library are reusable. Availability windows (fixed start and end times) are configured independently when assigning this test to a specific context (such as <strong>Scholarship Tests</strong>, <strong>Test Series</strong>, <strong>Live Classes / Batches</strong>, <strong>Test of the Day</strong>, or <strong>Recorded Courses</strong>).
        </p>
      </div>
    </div>
  </div>
);
};

export default TestView;